import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp } from '@capacitor/app'

export type Platform = 'ios-native' | 'android-native' | 'ios-web' | 'android-web' | 'desktop-web'
export type DeviceType = 'phone' | 'tablet' | 'desktop'

export interface DeviceInfo {
  platform: Platform
  deviceType: DeviceType
  appVersion: string | null // null for web
  webVersion: string
  userAgent: string
}

/**
 * Detects the platform (native app or web, and which OS)
 */
function detectPlatform(): Platform {
  const isNative = Capacitor.isNativePlatform()
  const capacitorPlatform = Capacitor.getPlatform()
  const userAgent = window.navigator.userAgent.toLowerCase()

  if (isNative && capacitorPlatform === 'ios') {
    return 'ios-native'
  } else if (isNative && capacitorPlatform === 'android') {
    return 'android-native'
  } else if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios-web'
  } else if (/android/.test(userAgent)) {
    return 'android-web'
  } else {
    return 'desktop-web'
  }
}

/**
 * Detects device type (phone, tablet, or desktop) from user agent and viewport
 */
function detectDeviceType(): DeviceType {
  const userAgent = window.navigator.userAgent.toLowerCase()
  
  // Check for tablets (before phone check since tablets also contain 'mobile')
  if (/ipad|android(?!.*mobile)/.test(userAgent)) {
    return 'tablet'
  }
  
  // Check for phones
  if (/iphone|android.*mobile|windows phone|blackberry|webos/.test(userAgent)) {
    return 'phone'
  }
  
  // Default to desktop
  return 'desktop'
}

/**
 * Gets the app version for native apps (iOS/Android)
 * Returns null for web apps
 */
async function getAppVersion(): Promise<string | null> {
  try {
    const isNative = Capacitor.isNativePlatform()
    if (!isNative) {
      return null
    }

    const info = await CapacitorApp.getInfo()
    return info.version
  } catch (error) {
    console.error('[DeviceInfo] Failed to get app version:', error)
    return null
  }
}

/**
 * Gets the web version from environment variable (set at build time)
 * This will be the extended version like "1.0.1.817"
 */
function getWebVersion(): string {
  // This will be replaced at build time with the extended version from git commit count
  return import.meta.env.VITE_APP_VERSION || 'unknown'
}

/**
 * Collects all device information
 * This is the main function to call to get complete device info
 */
export async function getDeviceInfo(): Promise<DeviceInfo> {
  const platform = detectPlatform()
  const deviceType = detectDeviceType()
  const appVersion = await getAppVersion()
  const webVersion = getWebVersion()
  const userAgent = window.navigator.userAgent

  return {
    platform,
    deviceType,
    appVersion,
    webVersion,
    userAgent
  }
}

/**
 * Logs device info to console for debugging (only in dev or for debug users)
 */
export function logDeviceInfoDebug(deviceInfo: DeviceInfo): void {
  console.log('[DeviceInfo]', {
    platform: deviceInfo.platform,
    deviceType: deviceInfo.deviceType,
    appVersion: deviceInfo.appVersion,
    webVersion: deviceInfo.webVersion
  })
}

